import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  Lock, LogOut, Users, Store, MessageSquare, FileText, Tags, Loader2,
  Download, Trash2, CheckCircle2, XCircle, BadgeCheck, Eye, EyeOff, Plus, ShieldCheck, KeyRound, BarChart3,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import Seo from "@/components/Seo";
import { apiFetch, postJSON, logoutRequest } from "@/lib/api";

const TABS = [
  { id: "visao", label: "Visão geral", icon: BarChart3 },
  { id: "leads", label: "Leads", icon: Users },
  { id: "fornecedores", label: "Fornecedores", icon: Store },
  { id: "contatos", label: "Contatos", icon: MessageSquare },
  { id: "conteudos", label: "Conteúdos", icon: FileText },
  { id: "categorias", label: "Categorias", icon: Tags },
  { id: "conta", label: "Conta", icon: KeyRound },
];

const ETAPA_ORDER = ["Planejamento", "Gestação", "Parto", "Pós-parto"];
const ETAPAS = ETAPA_ORDER;
const CHART_COLORS = ["#5C7A6B", "#D98B98", "#B59A6A", "#3D6455", "#8FA99B", "#C98B99", "#6F5C5F", "#A8C0B3"];

const toChartData = (obj = {}, order = null) => {
  const entries = Object.entries(obj);
  if (order) {
    entries.sort((a, b) => {
      const ia = order.indexOf(a[0]); const ib = order.indexOf(b[0]);
      return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    });
  } else {
    entries.sort((a, b) => b[1] - a[1]);
  }
  return entries.map(([name, value]) => ({ name, value }));
};

const StatCard = ({ label, value }) => (
  <div className="bg-white rounded-2xl border border-maternnia-nevoa/60 p-5">
    <p className="text-xs font-medium tracking-wide uppercase text-maternnia-taupe">{label}</p>
    <p className="mt-1 font-serif text-3xl text-maternnia-charcoal font-medium">{value}</p>
  </div>
);

const ChartCard = ({ title, data, testid }) => (
  <div data-testid={testid} className="bg-white rounded-2xl border border-maternnia-nevoa/60 p-5">
    <h3 className="font-serif text-lg text-maternnia-charcoal font-medium mb-4">{title}</h3>
    {data.length === 0 ? (
      <p className="text-sm text-maternnia-taupe font-light py-8 text-center">Ainda sem dados.</p>
    ) : (
      <ResponsiveContainer width="100%" height={230}>
        <BarChart data={data} margin={{ top: 4, right: 8, left: -18, bottom: 4 }}>
          <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#6F5C5F" }} interval={0} tickLine={false} axisLine={{ stroke: "#EADCDF" }} />
          <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#6F5C5F" }} tickLine={false} axisLine={false} />
          <Tooltip cursor={{ fill: "#F6E9EB" }} contentStyle={{ borderRadius: 12, border: "1px solid #EADCDF", fontSize: 12 }} />
          <Bar dataKey="value" radius={[6, 6, 0, 0]} maxBarSize={56}>
            {data.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    )}
  </div>
);

const DashboardPanel = () => {
  const [stats, setStats] = useState(null);
  useEffect(() => { apiFetch("/admin/stats").then(setStats).catch((e) => toast.error(e.message)); }, []);
  if (!stats) return <Loader2 className="animate-spin text-maternnia-eucalipto mx-auto mt-16" />;
  const t = stats.totais || {};
  return (
    <div data-testid="panel-visao" className="space-y-6">
      <h2 className="font-serif text-2xl text-maternnia-charcoal font-medium">Visão geral</h2>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Famílias" value={t.familias ?? 0} />
        <StatCard label="Fornecedores" value={t.fornecedores ?? 0} />
        <StatCard label="Contatos" value={t.contatos ?? 0} />
        <StatCard label="Artigos publicados" value={t.artigos ?? 0} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <ChartCard title="Famílias cadastradas (por etapa)" testid="chart-familias" data={toChartData(stats.familias_por_etapa, ETAPA_ORDER)} />
        <ChartCard title="Fornecedores cadastrados (por tipo)" testid="chart-fornecedores" data={toChartData(stats.fornecedores_por_tipo)} />
        <ChartCard title="Artigos publicados (por etapa)" testid="chart-artigos" data={toChartData(stats.artigos_por_etapa, ETAPA_ORDER)} />
        <ChartCard title="Contatos recebidos" testid="chart-contatos" data={[{ name: "Contatos", value: stats.contatos_total || 0 }]} />
      </div>
    </div>
  );
};

/* ---------------- Login ---------------- */
const Login = ({ onSuccess }) => {
  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await postJSON("/auth/login", { email, password: senha });
      onSuccess();
    } catch (err) {
      toast.error("Falha no login", { description: err.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="admin-login" className="min-h-screen bg-maternnia-charcoal flex items-center justify-center px-6">
      <div className="w-full max-w-sm bg-white rounded-[2rem] p-8 md:p-10 shadow-2xl">
        <div className="h-12 w-12 rounded-2xl bg-maternnia-eucalipto/10 flex items-center justify-center mb-6">
          <Lock size={22} className="text-maternnia-eucalipto" />
        </div>
        <h1 className="font-serif text-3xl text-maternnia-charcoal font-medium">Painel Maternnia</h1>
        <p className="mt-2 text-sm text-maternnia-taupe font-light">Acesso restrito à administração.</p>
        <form onSubmit={submit} className="mt-7 space-y-4">
          <div>
            <label htmlFor="admin-email" className="block text-sm font-medium text-maternnia-charcoal mb-1.5">E-mail</label>
            <input id="admin-email" data-testid="admin-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
              className="w-full border border-maternnia-nevoa rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-maternnia-eucalipto/30 focus:border-maternnia-eucalipto" />
          </div>
          <div>
            <label htmlFor="admin-senha" className="block text-sm font-medium text-maternnia-charcoal mb-1.5">Senha</label>
            <input id="admin-senha" data-testid="admin-senha" type="password" value={senha} onChange={(e) => setSenha(e.target.value)} required
              className="w-full border border-maternnia-nevoa rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-maternnia-eucalipto/30 focus:border-maternnia-eucalipto" />
          </div>
          <button type="submit" data-testid="admin-login-btn" disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 bg-maternnia-eucalipto text-white rounded-full px-6 py-3.5 text-sm font-medium hover:bg-maternnia-floresta transition-all duration-300 disabled:opacity-70">
            {loading ? <Loader2 size={18} className="animate-spin" /> : "Entrar"}
          </button>
        </form>
      </div>
    </main>
  );
};

/* ---------------- Generic UI ---------------- */
const Empty = ({ children }) => (
  <div className="text-center py-16 text-maternnia-taupe font-light text-sm">{children}</div>
);
const Th = ({ children }) => <th className="text-left text-xs font-semibold uppercase tracking-wide text-maternnia-taupe px-4 py-3">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 text-sm text-maternnia-charcoal align-top ${className}`}>{children}</td>;
const Card = ({ children }) => <div className="bg-white rounded-2xl border border-maternnia-nevoa/60 overflow-hidden">{children}</div>;

const Badge = ({ tone, children }) => {
  const tones = {
    green: "bg-emerald-50 text-emerald-700",
    red: "bg-red-50 text-red-600",
    amber: "bg-amber-50 text-amber-700",
    gray: "bg-maternnia-nevoa/50 text-maternnia-taupe",
  };
  return <span className={`inline-block text-xs font-medium px-2.5 py-1 rounded-full ${tones[tone] || tones.gray}`}>{children}</span>;
};

/* ---------------- Leads ---------------- */
const LeadsPanel = () => {
  const [rows, setRows] = useState(null);
  const load = useCallback(async () => {
    try { setRows(await apiFetch("/admin/leads")); } catch (e) { toast.error(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Excluir este lead?")) return;
    try { await apiFetch(`/admin/leads/${id}`, { method: "DELETE" }); setRows((r) => r.filter((x) => x.id !== id)); toast.success("Excluído."); }
    catch (e) { toast.error(e.message); }
  };

  const exportCsv = async () => {
    try {
      const res = await apiFetch("/admin/leads/export");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "leads.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message); }
  };

  const exportXlsx = async () => {
    try {
      const res = await apiFetch("/admin/export/xlsx");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "maternnia-contatos.xlsx"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Excel gerado (abas: Mães e Famílias / Fornecedores).");
    } catch (e) { toast.error(e.message); }
  };

  if (!rows) return <Loader2 className="animate-spin text-maternnia-eucalipto mx-auto mt-16" />;
  return (
    <div data-testid="panel-leads">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-serif text-2xl text-maternnia-charcoal font-medium">Leads <span className="text-maternnia-taupe text-base font-sans">({rows.length})</span></h2>
        <div className="flex items-center gap-2">
          <button data-testid="export-xlsx-btn" onClick={exportXlsx} className="inline-flex items-center gap-2 text-sm font-medium bg-maternnia-eucalipto text-white rounded-full px-4 py-2.5 hover:opacity-90 transition">
            <Download size={16} /> Exportar Excel
          </button>
          <button data-testid="export-leads-btn" onClick={exportCsv} className="inline-flex items-center gap-2 text-sm font-medium bg-maternnia-charcoal text-white rounded-full px-4 py-2.5 hover:opacity-90 transition">
            <Download size={16} /> Exportar CSV
          </button>
        </div>
      </div>
      {rows.length === 0 ? <Empty>Nenhum lead ainda.</Empty> : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-maternnia-porcelana/40 border-b border-maternnia-nevoa/60">
                <tr><Th>Nome</Th><Th>Contato</Th><Th>Local</Th><Th>Etapa</Th><Th>Assuntos</Th><Th>Ações</Th></tr>
              </thead>
              <tbody className="divide-y divide-maternnia-nevoa/50">
                {rows.map((l) => (
                  <tr key={l.id} data-testid={`lead-row-${l.id}`} className="hover:bg-maternnia-porcelana/20">
                    <Td><span className="font-medium">{l.nome}</span></Td>
                    <Td>{l.email}<br /><span className="text-maternnia-taupe text-xs">{l.whatsapp}</span></Td>
                    <Td>{l.cidade}{l.bairro ? `, ${l.bairro}` : ""}</Td>
                    <Td><Badge tone="gray">{l.etapa}</Badge></Td>
                    <Td className="text-xs text-maternnia-taupe">{(l.assuntos || []).join(", ")}</Td>
                    <Td><button data-testid={`lead-excluir-${l.id}`} onClick={() => remove(l.id)} className="text-red-600 p-1.5 rounded-full hover:bg-red-50 transition"><Trash2 size={15} /></button></Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
};

/* ---------------- Fornecedores ---------------- */
const FornecedoresPanel = () => {
  const [rows, setRows] = useState(null);
  const load = useCallback(async () => {
    try { setRows(await apiFetch("/admin/fornecedores")); } catch (e) { toast.error(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const patch = async (id, body) => {
    try {
      const updated = await apiFetch(`/admin/fornecedores/${id}`, { method: "PATCH", body: JSON.stringify(body) });
      setRows((r) => r.map((x) => (x.id === id ? updated : x)));
      toast.success("Atualizado.");
    } catch (e) { toast.error(e.message); }
  };
  const remove = async (id) => {
    if (!window.confirm("Excluir este fornecedor?")) return;
    try { await apiFetch(`/admin/fornecedores/${id}`, { method: "DELETE" }); setRows((r) => r.filter((x) => x.id !== id)); toast.success("Excluído."); }
    catch (e) { toast.error(e.message); }
  };

  if (!rows) return <Loader2 className="animate-spin text-maternnia-eucalipto mx-auto mt-16" />;
  return (
    <div data-testid="panel-fornecedores">
      <h2 className="font-serif text-2xl text-maternnia-charcoal font-medium mb-5">Fornecedores <span className="text-maternnia-taupe text-base font-sans">({rows.length})</span></h2>
      {rows.length === 0 ? <Empty>Nenhum fornecedor cadastrado.</Empty> : (
        <div className="space-y-4">
          {rows.map((f) => (
            <div key={f.id} data-testid={`fornecedor-row-${f.id}`} className="bg-white rounded-2xl border border-maternnia-nevoa/60 p-5">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-medium text-maternnia-charcoal">{f.empresa || f.nome}</h3>
                    {f.verificado && <BadgeCheck size={16} className="text-maternnia-eucalipto" />}
                    <Badge tone={f.curadoria === "aprovado" ? "green" : f.curadoria === "recusado" ? "red" : "amber"}>{f.curadoria || "pendente"}</Badge>
                    {f.publicado ? <Badge tone="green">Publicado</Badge> : <Badge tone="gray">Não publicado</Badge>}
                  </div>
                  <p className="text-sm text-maternnia-taupe mt-1">{f.categoria} • {f.cidade}{f.bairro ? `, ${f.bairro}` : ""}</p>
                  <p className="text-xs text-maternnia-taupe mt-1">{f.nome} • {f.email} • {f.whatsapp}</p>
                  {f.descricao && <p className="text-sm text-maternnia-charcoal/80 mt-2 font-light max-w-2xl">{f.descricao}</p>}
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-4">
                <button data-testid={`forn-aprovar-${f.id}`} onClick={() => patch(f.id, { curadoria: "aprovado" })} className="inline-flex items-center gap-1.5 text-xs font-medium bg-emerald-600 text-white rounded-full px-3 py-2 hover:opacity-90 transition"><CheckCircle2 size={14} /> Aprovar</button>
                <button data-testid={`forn-recusar-${f.id}`} onClick={() => patch(f.id, { curadoria: "recusado" })} className="inline-flex items-center gap-1.5 text-xs font-medium bg-red-500 text-white rounded-full px-3 py-2 hover:opacity-90 transition"><XCircle size={14} /> Recusar</button>
                <button data-testid={`forn-publicar-${f.id}`} onClick={() => patch(f.id, { publicado: !f.publicado })} className="inline-flex items-center gap-1.5 text-xs font-medium bg-maternnia-charcoal text-white rounded-full px-3 py-2 hover:opacity-90 transition">{f.publicado ? <><EyeOff size={14} /> Despublicar</> : <><Eye size={14} /> Publicar</>}</button>
                <button data-testid={`forn-verificar-${f.id}`} onClick={() => patch(f.id, { verificado: !f.verificado })} className="inline-flex items-center gap-1.5 text-xs font-medium bg-white border border-maternnia-nevoa text-maternnia-charcoal rounded-full px-3 py-2 hover:bg-maternnia-porcelana/40 transition"><BadgeCheck size={14} /> {f.verificado ? "Remover selo" : "Verificar"}</button>
                <button data-testid={`forn-excluir-${f.id}`} onClick={() => remove(f.id)} className="inline-flex items-center gap-1.5 text-xs font-medium text-red-600 rounded-full px-3 py-2 hover:bg-red-50 transition"><Trash2 size={14} /> Excluir</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ---------------- Contatos ---------------- */
const ContatosPanel = () => {
  const [rows, setRows] = useState(null);
  const load = useCallback(async () => { try { setRows(await apiFetch("/admin/contatos")); } catch (e) { toast.error(e.message); } }, []);
  useEffect(() => { load(); }, [load]);
  const remove = async (id) => {
    if (!window.confirm("Excluir este contato?")) return;
    try { await apiFetch(`/admin/contatos/${id}`, { method: "DELETE" }); setRows((r) => r.filter((x) => x.id !== id)); toast.success("Excluído."); }
    catch (e) { toast.error(e.message); }
  };
  if (!rows) return <Loader2 className="animate-spin text-maternnia-eucalipto mx-auto mt-16" />;
  return (
    <div data-testid="panel-contatos">
      <h2 className="font-serif text-2xl text-maternnia-charcoal font-medium mb-5">Contatos <span className="text-maternnia-taupe text-base font-sans">({rows.length})</span></h2>
      {rows.length === 0 ? <Empty>Nenhum pedido de contato.</Empty> : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-maternnia-porcelana/40 border-b border-maternnia-nevoa/60"><tr><Th>Nome</Th><Th>E-mail</Th><Th>Profissional</Th><Th>Mensagem</Th><Th>Ações</Th></tr></thead>
              <tbody className="divide-y divide-maternnia-nevoa/50">
                {rows.map((c) => (
                  <tr key={c.id} data-testid={`contato-row-${c.id}`} className="hover:bg-maternnia-porcelana/20"><Td>{c.nome || "—"}</Td><Td>{c.email || "—"}</Td><Td>{c.profissionalNome || "—"}</Td><Td className="text-xs text-maternnia-taupe max-w-sm">{c.mensagem || "—"}</Td><Td><button data-testid={`contato-excluir-${c.id}`} onClick={() => remove(c.id)} className="text-red-600 p-1.5 rounded-full hover:bg-red-50 transition"><Trash2 size={15} /></button></Td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
};

/* ---------------- Conteúdos ---------------- */
const EMPTY_CONTEUDO = { titulo: "", resumo: "", etapa: "Planejamento", categoria: "", autor: "", revisor: "", imagem: "", corpo: "", referencias: "", data: "", publicado: false };

const ConteudosPanel = () => {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState(EMPTY_CONTEUDO);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = useCallback(async () => { try { setRows(await apiFetch("/admin/conteudos")); } catch (e) { toast.error(e.message); } }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const resetForm = () => { setForm(EMPTY_CONTEUDO); setEditingId(null); };

  const save = async (publicado) => {
    if (!form.titulo.trim()) { toast.error("Informe o título."); return; }
    setSaving(true);
    const payload = { ...form, publicado };
    try {
      if (editingId) {
        const upd = await apiFetch(`/admin/conteudos/${editingId}`, { method: "PUT", body: JSON.stringify(payload) });
        setRows((r) => r.map((x) => (x.id === editingId ? upd : x)));
        toast.success(publicado ? "Conteúdo publicado." : "Rascunho salvo.");
      } else {
        const created = await apiFetch("/admin/conteudos", { method: "POST", body: JSON.stringify(payload) });
        setRows((r) => [created, ...r.filter((x) => x.id !== created.id)]);
        toast.success(publicado ? "Conteúdo publicado." : "Rascunho criado.");
      }
      resetForm();
    } catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };

  const editar = (c) => {
    setEditingId(c.id);
    setForm({ titulo: c.titulo || "", resumo: c.resumo || "", etapa: c.etapa || "Planejamento", categoria: c.categoria || "", autor: c.autor || "", revisor: c.revisor || "", imagem: c.imagem || "", corpo: c.corpo || "", referencias: c.referencias || "", data: c.data || "", publicado: !!c.publicado });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const togglePublicar = async (c) => {
    try {
      const upd = await apiFetch(`/admin/conteudos/${c.id}`, { method: "PUT", body: JSON.stringify({ ...c, publicado: !c.publicado }) });
      setRows((r) => r.map((x) => (x.id === c.id ? upd : x)));
      toast.success(!c.publicado ? "Publicado." : "Despublicado.");
    } catch (e) { toast.error(e.message); }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir este conteúdo?")) return;
    try { await apiFetch(`/admin/conteudos/${id}`, { method: "DELETE" }); setRows((r) => r.filter((x) => x.id !== id)); if (editingId === id) resetForm(); toast.success("Excluído."); }
    catch (e) { toast.error(e.message); }
  };

  const inp = "w-full border border-maternnia-nevoa rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-maternnia-eucalipto/30 focus:border-maternnia-eucalipto";
  const lbl = "block text-xs font-medium text-maternnia-taupe mb-1";
  return (
    <div data-testid="panel-conteudos">
      <h2 className="font-serif text-2xl text-maternnia-charcoal font-medium mb-5">Conteúdos</h2>

      <form onSubmit={(e) => e.preventDefault()} data-testid="conteudo-form" className="bg-white rounded-2xl border border-maternnia-nevoa/60 p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-medium text-maternnia-charcoal">{editingId ? "Editar conteúdo" : "Novo conteúdo"}</h3>
          {editingId && <button type="button" data-testid="conteudo-cancelar" onClick={resetForm} className="text-xs text-maternnia-taupe hover:text-maternnia-charcoal underline">Cancelar edição</button>}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2"><label className={lbl}>Título *</label><input data-testid="conteudo-titulo" className={inp} value={form.titulo} onChange={(e) => set("titulo", e.target.value)} /></div>
          <div><label className={lbl}>Etapa</label><select className={inp} value={form.etapa} onChange={(e) => set("etapa", e.target.value)}>{ETAPAS.map((x) => <option key={x}>{x}</option>)}</select></div>
          <div><label className={lbl}>Categoria</label><input className={inp} value={form.categoria} onChange={(e) => set("categoria", e.target.value)} /></div>
          <div><label className={lbl}>Autor</label><input data-testid="conteudo-autor" className={inp} value={form.autor} onChange={(e) => set("autor", e.target.value)} /></div>
          <div><label className={lbl}>Revisor</label><input className={inp} value={form.revisor} onChange={(e) => set("revisor", e.target.value)} /></div>
          <div><label className={lbl}>Imagem (URL)</label><input className={inp} value={form.imagem} onChange={(e) => set("imagem", e.target.value)} placeholder="https://..." /></div>
          <div><label className={lbl}>Data</label><input className={inp} type="date" value={form.data} onChange={(e) => set("data", e.target.value)} /></div>
          <div className="md:col-span-2"><label className={lbl}>Resumo</label><input className={inp} value={form.resumo} onChange={(e) => set("resumo", e.target.value)} /></div>
          <div className="md:col-span-2"><label className={lbl}>Corpo</label><textarea className={`${inp} resize-none`} rows={5} value={form.corpo} onChange={(e) => set("corpo", e.target.value)} /></div>
          <div className="md:col-span-2"><label className={lbl}>Referências</label><textarea className={`${inp} resize-none`} rows={2} value={form.referencias} onChange={(e) => set("referencias", e.target.value)} placeholder="Uma referência por linha" /></div>
        </div>
        <div className="flex flex-wrap gap-3 mt-5">
          <button type="button" data-testid="conteudo-rascunho-btn" disabled={saving} onClick={() => save(false)} className="inline-flex items-center gap-2 bg-white border border-maternnia-nevoa text-maternnia-charcoal rounded-full px-5 py-2.5 text-sm font-medium hover:bg-maternnia-porcelana/40 transition disabled:opacity-70">
            {saving ? <Loader2 size={16} className="animate-spin" /> : "Salvar rascunho"}
          </button>
          <button type="button" data-testid="conteudo-publicar-btn" disabled={saving} onClick={() => save(true)} className="inline-flex items-center gap-2 bg-maternnia-eucalipto text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-maternnia-floresta transition disabled:opacity-70">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <><Plus size={16} /> {editingId ? "Salvar e publicar" : "Publicar"}</>}
          </button>
        </div>
      </form>

      {!rows ? <Loader2 className="animate-spin text-maternnia-eucalipto mx-auto mt-8" /> : rows.length === 0 ? <Empty>Nenhum conteúdo cadastrado.</Empty> : (
        <div className="space-y-3">
          {rows.map((c) => (
            <div key={c.id} data-testid={`conteudo-row-${c.id}`} className="bg-white rounded-2xl border border-maternnia-nevoa/60 p-4 flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap"><h3 className="font-medium text-maternnia-charcoal">{c.titulo}</h3>{c.publicado ? <Badge tone="green">Publicado</Badge> : <Badge tone="amber">Rascunho</Badge>}</div>
                <p className="text-xs text-maternnia-taupe mt-1">{c.etapa}{c.categoria ? ` • ${c.categoria}` : ""}{c.autor ? ` • por ${c.autor}` : ""}</p>
                {c.resumo && <p className="text-sm text-maternnia-charcoal/80 mt-1 font-light">{c.resumo}</p>}
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <button data-testid={`conteudo-editar-${c.id}`} onClick={() => editar(c)} className="text-xs font-medium px-3 py-2 rounded-full border border-maternnia-nevoa text-maternnia-charcoal hover:bg-maternnia-porcelana/40 transition">Editar</button>
                <button data-testid={`conteudo-toggle-${c.id}`} onClick={() => togglePublicar(c)} className="text-xs font-medium px-3 py-2 rounded-full bg-maternnia-charcoal text-white hover:opacity-90 transition">{c.publicado ? "Despublicar" : "Publicar"}</button>
                <button data-testid={`conteudo-excluir-${c.id}`} onClick={() => remove(c.id)} className="text-red-600 p-2 rounded-full hover:bg-red-50 transition"><Trash2 size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ---------------- Categorias ---------------- */
const CategoriasPanel = () => {
  const [rows, setRows] = useState(null);
  const [nome, setNome] = useState("");
  const [tipo, setTipo] = useState("profissional");
  const load = useCallback(async () => { try { setRows(await apiFetch("/admin/categorias")); } catch (e) { toast.error(e.message); } }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!nome.trim()) return;
    try { const c = await apiFetch("/admin/categorias", { method: "POST", body: JSON.stringify({ nome, tipo }) }); setRows((r) => [...r, c]); setNome(""); toast.success("Categoria criada."); }
    catch (err) { toast.error(err.message); }
  };
  const remove = async (id) => {
    try { await apiFetch(`/admin/categorias/${id}`, { method: "DELETE" }); setRows((r) => r.filter((x) => x.id !== id)); }
    catch (e) { toast.error(e.message); }
  };
  const inp = "border border-maternnia-nevoa rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-maternnia-eucalipto/30 focus:border-maternnia-eucalipto";
  return (
    <div data-testid="panel-categorias">
      <h2 className="font-serif text-2xl text-maternnia-charcoal font-medium mb-5">Categorias</h2>
      <form onSubmit={create} className="flex flex-wrap gap-3 mb-6">
        <input data-testid="categoria-nome" className={inp} placeholder="Nova categoria" value={nome} onChange={(e) => setNome(e.target.value)} />
        <select className={inp} value={tipo} onChange={(e) => setTipo(e.target.value)}><option value="profissional">Profissional</option><option value="conteudo">Conteúdo</option><option value="etapa">Etapa</option></select>
        <button data-testid="categoria-criar-btn" type="submit" className="inline-flex items-center gap-2 bg-maternnia-eucalipto text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-maternnia-floresta transition"><Plus size={16} /> Adicionar</button>
      </form>
      {!rows ? <Loader2 className="animate-spin text-maternnia-eucalipto mx-auto mt-8" /> : rows.length === 0 ? <Empty>Nenhuma categoria.</Empty> : (
        <div className="flex flex-wrap gap-2">
          {rows.map((c) => (
            <span key={c.id} data-testid={`categoria-${c.id}`} className="inline-flex items-center gap-2 bg-white border border-maternnia-nevoa rounded-full pl-4 pr-2 py-2 text-sm text-maternnia-charcoal">
              {c.nome} <span className="text-xs text-maternnia-taupe">({c.tipo})</span>
              <button onClick={() => remove(c.id)} className="text-red-500 hover:bg-red-50 rounded-full p-1 transition"><Trash2 size={13} /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

/* ---------------- Conta (alterar senha) ---------------- */
const ContaPanel = () => {
  const [atual, setAtual] = useState("");
  const [nova, setNova] = useState("");
  const [confirma, setConfirma] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (nova !== confirma) { toast.error("As senhas não coincidem."); return; }
    if (nova.length < 10) { toast.error("A nova senha deve ter ao menos 10 caracteres."); return; }
    setSaving(true);
    try {
      await apiFetch("/auth/change-password", { method: "POST", body: JSON.stringify({ current_password: atual, new_password: nova }) });
      toast.success("Senha alterada com sucesso.");
      setAtual(""); setNova(""); setConfirma("");
    } catch (err) { toast.error("Não foi possível alterar", { description: err.message }); }
    finally { setSaving(false); }
  };

  const inp = "w-full border border-maternnia-nevoa rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-maternnia-eucalipto/30 focus:border-maternnia-eucalipto";
  return (
    <div data-testid="panel-conta" className="max-w-md">
      <h2 className="font-serif text-2xl text-maternnia-charcoal font-medium mb-1">Conta</h2>
      <p className="text-sm text-maternnia-taupe font-light mb-6">Altere a senha de acesso ao painel.</p>
      <form onSubmit={submit} className="bg-white rounded-2xl border border-maternnia-nevoa/60 p-6 space-y-4">
        <div>
          <label htmlFor="cp-atual" className="block text-sm font-medium text-maternnia-charcoal mb-1.5">Senha atual</label>
          <input id="cp-atual" data-testid="cp-atual" type="password" required value={atual} onChange={(e) => setAtual(e.target.value)} className={inp} />
        </div>
        <div>
          <label htmlFor="cp-nova" className="block text-sm font-medium text-maternnia-charcoal mb-1.5">Nova senha</label>
          <input id="cp-nova" data-testid="cp-nova" type="password" required value={nova} onChange={(e) => setNova(e.target.value)} className={inp} />
          <p className="mt-1 text-xs text-maternnia-taupe font-light">Mínimo 10 caracteres, combinando letras e números.</p>
        </div>
        <div>
          <label htmlFor="cp-confirma" className="block text-sm font-medium text-maternnia-charcoal mb-1.5">Confirmar nova senha</label>
          <input id="cp-confirma" data-testid="cp-confirma" type="password" required value={confirma} onChange={(e) => setConfirma(e.target.value)} className={inp} />
        </div>
        <button data-testid="cp-submit" type="submit" disabled={saving} className="w-full inline-flex items-center justify-center gap-2 bg-maternnia-eucalipto text-white rounded-full px-5 py-3 text-sm font-medium hover:bg-maternnia-floresta transition disabled:opacity-70">
          {saving ? <Loader2 size={16} className="animate-spin" /> : "Alterar senha"}
        </button>
      </form>
    </div>
  );
};

/* ---------------- Shell ---------------- */
const AdminPage = () => {
  const [authed, setAuthed] = useState(false);
  const [tab, setTab] = useState("visao");
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    (async () => {
      try { await apiFetch("/auth/me"); setAuthed(true); }
      catch { setAuthed(false); }
      finally { setChecking(false); }
    })();
    const onExpired = () => setAuthed(false);
    window.addEventListener("mat-auth-expired", onExpired);
    return () => window.removeEventListener("mat-auth-expired", onExpired);
  }, []);

  const logout = async () => { await logoutRequest(); setAuthed(false); setTab("visao"); };

  if (checking) return <main className="min-h-screen flex items-center justify-center bg-maternnia-charcoal"><Loader2 className="animate-spin text-white" /></main>;
  if (!authed) return (<><Seo title="Painel" noindex /><Login onSuccess={() => setAuthed(true)} /></>);

  const Panel = { visao: DashboardPanel, leads: LeadsPanel, fornecedores: FornecedoresPanel, contatos: ContatosPanel, conteudos: ConteudosPanel, categorias: CategoriasPanel, conta: ContaPanel }[tab];

  return (
    <main data-testid="admin-dashboard" className="min-h-screen bg-maternnia-porcelana/30 flex">
      <Seo title="Painel" noindex />
      <aside className="w-60 bg-maternnia-charcoal text-white flex flex-col shrink-0 min-h-screen sticky top-0">
        <div className="px-6 py-7 flex items-center gap-2 border-b border-white/10">
          <ShieldCheck size={20} className="text-maternnia-pink" />
          <span className="font-serif text-xl">Maternnia</span>
        </div>
        <nav className="flex-1 px-3 py-5 space-y-1">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setTab(t.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors duration-200 ${tab === t.id ? "bg-white/10 text-white" : "text-white/65 hover:text-white hover:bg-white/5"}`}>
                <Icon size={18} /> {t.label}
              </button>
            );
          })}
        </nav>
        <button data-testid="admin-logout" onClick={logout} className="m-3 flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-white/65 hover:text-white hover:bg-white/5 transition-colors">
          <LogOut size={18} /> Sair
        </button>
      </aside>
      <section className="flex-1 px-6 md:px-10 py-8 md:py-10 overflow-x-hidden">
        <Panel />
      </section>
    </main>
  );
};

export default AdminPage;
